import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { purchaseWithExistingCard } from 'redux/thunks/purchase-with-existing-card'
import railsApiService from 'services/api/rails-api-service'
import storefrontApiService, {
  StorefrontBaseAttribute,
  StorefrontCart,
  StorefrontCheckoutParams
} from 'services/api/storefront-api-service'

export interface PhytotherapyState {
  isLoading: boolean
  products: Products | null
  activeVariant: StorefrontProductVariant | null
  orderData: StorefrontOrderData | null
}

const initialState: PhytotherapyState = {
  isLoading: false,
  products: null,
  activeVariant: null,
  orderData: null
}

export const phytotherapySlice = createSlice({
  name: 'phytotherapy',
  initialState,
  reducers: {
    resetPhytotherapyState: (state) => {
      state = initialState
      return state
    },
    setActiveVariant: (state, action: PayloadAction<StorefrontProductVariant | null>) => {
      state.activeVariant = action.payload
    }
  },
  extraReducers: (builder) =>
    builder
      .addCase(getProducts.pending, (state) => {
        state.isLoading = true
      })
      .addCase(getProducts.fulfilled, (state, action) => {
        state.isLoading = false
        state.products = action.payload
      })
      .addCase(getProducts.rejected, (state) => {
        state.isLoading = false
      })
      .addCase(phytotherapyCreateOrder.fulfilled, (state, action) => {
        state.isLoading = false
        state.orderData = action.payload
      })
      .addCase(phytotherapyCreateDelivery.fulfilled, (state, action) => {
        state.isLoading = false
        if (state.orderData) state.orderData.cart = action.payload
      })
})

export const { resetPhytotherapyState, setActiveVariant } = phytotherapySlice.actions

export default phytotherapySlice.reducer

export const getProducts = createAsyncThunk('get-products', async (_, { rejectWithValue }) => {
  try {
    return await storefrontApiService.getProducts()
  } catch (error) {
    return rejectWithValue(error)
  }
})

interface PhytotherapyCheckoutParams {
  variant_id: string
}

export const phytotherapyCreateOrder = createAsyncThunk(
  'phytotherapy-create-order',
  async ({ variant_id }: PhytotherapyCheckoutParams, { rejectWithValue }) => {
    try {
      return await storefrontApiService.phytotherapyCreateOrder(variant_id)
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

interface PhytotherapyCreateDeliveryParams {
  firstname: string
  lastname: string
  address1: string
  address2: string
  zipcode: string
  city: string
  country_iso: string
}

export const phytotherapyCreateDelivery = createAsyncThunk(
  'phytotherapy-create-delivery',
  async (params: PhytotherapyCreateDeliveryParams, { rejectWithValue }) => {
    try {
      const { data } = await railsApiService.apiInstance.patch('/api/v2/storefront/phyto_checkout/create_delivery', {
        shipping_address: params
      })

      return data
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export const phytotherapyCheckout = createAsyncThunk(
  'phytotherapy-checkout',
  async (params: StorefrontCheckoutParams, { rejectWithValue }) => {
    try {
      return await storefrontApiService.checkout(params)
    } catch (error) {
      return rejectWithValue(error)
    }
  }
)

export interface Products {
  product: Record<string, Product>
  image: Record<string, Image>
  variant: Record<string, StorefrontProductVariant>
  optionValue: Record<string, OptionValue>
  optionType: Record<string, OptionType>
}

export interface Image {
  id: string
  type: string
  attributes: ImageAttributes
}

export interface ImageAttributes {
  viewableType: string
  viewableId: number
  styles: Style[]
}

export interface Style {
  url: string
  width: string
  height: string
}

export interface OptionType {
  id: string
  type: string
  attributes: OptionTypeAttributes
  relationships: OptionTypeRelationships
}

export interface OptionTypeAttributes {
  name: string
  presentation: string
  position: number
}

export interface OptionTypeRelationships {
  optionValues: OptionValues
}

export interface OptionValues {
  data: StorefrontBaseAttribute[]
}

export interface OptionValue {
  id: string
  type: string
  attributes: OptionTypeAttributes
  relationships: OptionValueRelationships
}

export interface OptionValueRelationships {
  optionType: DefaultVariantClass
}

export interface DefaultVariantClass {
  data: StorefrontBaseAttribute
}

export interface Product {
  id: string
  type: string
  attributes: ProductAttributes
  relationships: ProductRelationships
}

export interface ProductAttributes {
  name: string
  description: null | string
  price: string
  currency: string
  displayPrice: string
  availableOn: string
  slug: string
  metaDescription: null | string
  metaKeywords: null | string
  updatedAt: string
  purchasable: boolean
  inStock: boolean
  backorderable: boolean
}

export interface ProductRelationships {
  variants: OptionValues
  optionTypes: OptionValues
  productProperties: OptionValues
  taxons: OptionValues
  images: OptionValues
  defaultVariant: DefaultVariantClass
  variantsIncludingMaster: OptionValues
}

export interface StorefrontProductVariant {
  id: string
  type: string
  attributes: StorefrontProductVariantAttributes
  relationships: StorefrontProductVariantRelationships
}

export interface StorefrontProductVariantAttributes {
  name: string
  sku: string
  price: string
  currency: string
  displayPrice: string
  weight: string
  height: null
  width: null
  depth: null
  isMaster: boolean
  optionsText: string
  slug: string
  description: null | string
  purchasable: boolean
  inStock: boolean
  backorderable: boolean
}

export interface StorefrontProductVariantRelationships {
  product: DefaultVariantClass
  images: OptionValues
  optionValues: OptionValues
}

export interface StorefrontOrderData {
  cart: Record<string, StorefrontCart>
  lineItem: Record<string, StorefrontLineItem>
}

export interface StorefrontLineItem {
  id: string
  type: string
  attributes: StorefrontLineItemAttributes
  relationships: StorefrontLineItemRelationships
}

export interface StorefrontLineItemAttributes {
  name: string
  quantity: number
  price: string
  slug: string
  optionsText: string
  currency: string
  displayPrice: string
  total: string
  displayTotal: string
  adjustmentTotal: string
  displayAdjustmentTotal: string
  additionalTaxTotal: string
  discountedAmount: string
  displayDiscountedAmount: string
  displayAdditionalTaxTotal: string
  promoTotal: string
  displayPromoTotal: string
  includedTaxTotal: string
  displayIncludedTaxTotal: string
}

export interface StorefrontLineItemRelationships {
  variant: StorefrontLineItemVariant
}

export interface StorefrontLineItemVariant {
  data: StorefrontBaseAttribute
}
